import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import type { GalleryItem } from "../../data/gallery";
type HorizontalAlbumProps = {
  items: GalleryItem[];
  onOpen: (id: string) => void;
};
const OFFSETS = ["mt-0", "mt-24", "mt-8", "mt-32", "mt-4", "mt-20", "mt-12"];
const HEIGHTS = ["h-[52vh]", "h-[40vh]", "h-[58vh]", "h-[36vh]", "h-[50vh]", "h-[44vh]", "h-[54vh]"];
export function HorizontalAlbum({
  items,
  onOpen
}: HorizontalAlbumProps) {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start start", "end end"]
  });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-72%"]);
  const counter = useTransform(scrollYProgress, v => String(Math.min(items.length, Math.max(1, Math.ceil(v * items.length)))).padStart(2, "0"));
  if (reduce) {
    return <section className="no-scrollbar flex gap-4 overflow-x-auto bg-[#061f38] px-5 py-20 md:px-9" data-id="element-225" data-mp="5u954urzujpm">
        {items.map(item => <button key={item.id} type="button" onClick={() => onOpen(item.id)} className="h-[50vh] w-[70vw] max-w-[420px] shrink-0 overflow-hidden" data-id="element-226" data-mp="218lvwcfcjrxz">
            <img src={item.src} alt={item.alt} loading="lazy" className="h-full w-full object-cover" data-id="element-227" data-mp="2a2qgie3w0752" />
          </button>)}
      </section>;
  }
  return <section ref={ref} className="relative h-[320vh] bg-[#061f38] text-white" aria-label="A year at Maple" data-id="element-228" data-mp="2f3xskpd2bshj">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden" data-id="element-229" data-mp="1mdfktb1e92xs">
        <div className="absolute left-5 right-5 top-24 flex items-baseline justify-between md:left-9 md:right-9" data-id="element-230" data-mp="1rciho87802uo">
          <p className="text-xs font-semibold tracking-[0.2em] text-[#f0b1b9]" data-id="element-231" data-mp="1wbbudyeonpts">A YEAR AT MAPLE · KEEP SCROLLING</p>
          <p className="text-sm tabular-nums text-white/60" data-id="element-232" data-mp="32sgvsqh99f2r"><motion.span data-id="element-233" data-mp="32trz6p6sgktc">{counter}</motion.span> / {String(items.length).padStart(2, "0")}</p>
        </div>
        <motion.div style={{
        x
      }} className="flex w-max items-start gap-6 pl-5 pr-[20vw] md:gap-10 md:pl-9" data-id="element-234" data-mp="37slfsqhdgrxq">
          <div className="flex h-[58vh] w-[80vw] max-w-[520px] shrink-0 flex-col justify-end pr-6" data-id="element-235" data-mp="2d3jxxjuj5xgn">
            <h2 className="text-[clamp(3rem,7vw,6.5rem)] font-medium leading-[0.88] tracking-[-0.075em]" data-id="element-236" data-mp="2s0sr8j3e9bb0">Every day leaves a picture.</h2>
            <p className="mt-6 max-w-xs text-base leading-7 text-white/60" data-id="element-237" data-mp="2n2rai4tfxo2p">Classrooms, courts, studios and celebrations: the rhythm of a school year in Lekki.</p>
          </div>
          {items.map((item, i) => <figure key={item.id} className={`shrink-0 ${OFFSETS[i % OFFSETS.length]}`} data-id="element-238" data-mp="1ued87cvbk5lc" data-mpi={i}>
              <button type="button" onClick={() => onOpen(item.id)} aria-label={`Open ${item.caption}`} className={`group relative block overflow-hidden ${HEIGHTS[i % HEIGHTS.length]} ${item.shape === "wide" ? "w-[70vw] md:w-[42vw]" : "w-[55vw] md:w-[24vw]"}`} data-id="element-239" data-mp="292zy28ikofqt">
                <img src={item.src} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" data-id="element-240" data-mp="1bmsufgzui1rz" />
              </button>
              <figcaption className="mt-3 flex justify-between gap-4 text-xs text-white/60" data-id="element-241" data-mp="3s0cqwe01ik6o">
                <span data-id="element-242" data-mp="3dbqf2m9mgak7">{item.caption}</span>
                <span className="text-[#f0b1b9]" data-id="element-243" data-mp="3dcrsgxe41953">{item.album}</span>
              </figcaption>
            </figure>)}
        </motion.div>
        <div className="absolute bottom-10 left-5 right-5 h-px bg-white/15 md:left-9 md:right-9" data-id="element-244" data-mp="3i8gpou2nqdy8">
          <motion.div style={{
          scaleX: scrollYProgress
        }} className="h-full origin-left bg-[#c5233d]" data-id="element-245" data-mp="9t6wkbgqwj2l" />
        </div>
      </div>
    </section>;
}